import React, { useMemo, useState } from "react";
import { Row, Col, Form, Select, Input, Button, Collapse, DatePicker } from "antd";
import type { FormInstance } from "antd";
import { v4 as uuid } from "uuid";
import { useAppSelector } from "../../..";
import type { AnyObject, DictionaryDto } from "../../../lib/interfaces";
import InputNumberFormat from "../../common/InputNumberFormat";
import { checkIsLoaded, checkIsLoading, displayFormat } from "../../../lib/utils";
import { IClientType, ISearchFilterField } from "../../../lib/types";
import { BulkFiltersToggler } from "../../BulkFiltersToggler/BulkFiltersToggler";
import { ActiveSearchFormItem, AgreementsSearchFormItem } from "../../BulkSearchItems";
import "./ChargesSearchFilters.less";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const isAdjustmentList = {
  0: "Начисление",
  1: "Корректировка",
};

interface ComponentProps {
  form: FormInstance;
  defaultSearchParams: AnyObject;
  onSearch: (values: AnyObject) => void;
  handleFilterService: (value: any) => void;
  serviceFiltered: DictionaryDto[];
  resetSearchPaymentsAndResetSetSelectedRowKeys: () => void;
  filtersCollapseState: string;
  setFiltersCollapseState: React.Dispatch<React.SetStateAction<string>>;
  clientType: IClientType;
  setClientType: React.Dispatch<React.SetStateAction<IClientType>>;
}

export const ChargesSearchFilters = (props: ComponentProps) => {
  const {
    form,
    defaultSearchParams,
    onSearch,
    resetSearchPaymentsAndResetSetSelectedRowKeys,
    filtersCollapseState,
    setFiltersCollapseState,
    clientType,
    setClientType,
  } = props;

  const { rateTypeLoading, rateModesEventsLoading } = useAppSelector(
    (state) => state.rtkDictionaries
  );
  const { chargesMassLoading } = useAppSelector((state) => state.charges);
  const { tenant } = useAppSelector((state) => state.tenants);
  const { serviceTypesFetchingState } = useAppSelector((state) => state.service);

  const rateType = (useAppSelector(
    (state) => state.rtkDictionaries.rateType
  ) as unknown) as DictionaryDto[];

  const rateModesEvents = (useAppSelector(
    (state) => state.rtkDictionaries.rateModesEvents
  ) as unknown) as DictionaryDto[];

  const serviceTypes = (useAppSelector(
    (state) => state.service.serviceTypes
  ) as unknown) as DictionaryDto[];

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapseState(newState);
  };

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Клиент",
        fieldElement: (
          <AgreementsSearchFormItem
            name="accountIds"
            multiselect
            combineAgreements
            clientType={clientType}
            setClientType={setClientType}
            onChange={() => {
              form.setFieldsValue({
                "organization.property.stringValue": undefined,
                "subscriber.externalId": undefined,
              });
            }}
          />
        ),
        visible: true,
      },
      {
        label: "Сумма начисления с",
        fieldElement: (
          <Form.Item label="Сумма начисления с">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="chargeAmountFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear />
                </Form.Item>
              </Col>
              <Col span={3} offset={1}>
                <label>по:</label>
              </Col>
              <Col span={10}>
                <Form.Item name="chargeAmountTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата СФ",
        fieldElement: (
          <Form.Item label="Дата СФ" name="invoiceDate">
            <DatePicker.RangePicker style={{ width: "100%" }} format={displayFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Вид цены",
        fieldElement: (
          <Form.Item label="Вид цены" name="rateModeId">
            <Select showSearch placeholder="Выбрать" allowClear>
              {checkIsLoaded(rateModesEventsLoading) &&
                rateModesEvents &&
                rateModesEvents.map((item) => (
                  <Select.Option key={item.id} value={item.id} id={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата формирования начислений",
        fieldElement: (
          <Form.Item label="Дата формирования начислений" name="chargeDate">
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={displayFormat}
              allowClear={false}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Номер счета",
        fieldElement: (
          <Form.Item label="Номер счета" name="billNumber">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип продукта",
        fieldElement: (
          <Form.Item label="Тип продукта" name="serviceTypeName">
            <Select placeholder="Выбрать" allowClear showSearch showArrow mode="multiple">
              {checkIsLoaded(serviceTypesFetchingState) &&
                serviceTypes &&
                serviceTypes.map((item) => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Период формирования начислений",
        fieldElement: (
          <Form.Item label="Период формирования начислений" name="chargePeriod">
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              format={displayFormat}
              allowClear={false}
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Сумма счета с",
        fieldElement: (
          <Form.Item label="Сумма счета с">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="billAmountFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear />
                </Form.Item>
              </Col>
              <Col span={3} offset={1}>
                <label>по:</label>
              </Col>
              <Col span={10}>
                <Form.Item name="billAmountTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип цены",
        fieldElement: (
          <Form.Item label="Тип цены" name="rateTypeId">
            <Select showSearch placeholder="Выбрать" allowClear>
              {checkIsLoaded(rateTypeLoading) &&
                rateType &&
                rateType.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Начисление/Корректировка",
        fieldElement: (
          <Form.Item label="Начисление/Корректировка" name="isAdjustment">
            <Select showSearch placeholder="Выбрать" allowClear>
              {isAdjustmentList &&
                Object.keys(isAdjustmentList).map((item) => (
                  <Select.Option key={uuid()} value={item}>
                    {isAdjustmentList[item]}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата счета",
        fieldElement: (
          <Form.Item label="Дата счета" name="billDate">
            <DatePicker.RangePicker style={{ width: "100%" }} format={displayFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Количество с",
        fieldElement: (
          <Form.Item label="Количество с">
            <Row align="middle">
              <Col span={10}>
                <Form.Item name="unitsFrom" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={0} />
                </Form.Item>
              </Col>
              <Col span={3} offset={1}>
                <label>по:</label>
              </Col>
              <Col span={10}>
                <Form.Item name="unitsTo" style={{ marginBottom: "0" }}>
                  <InputNumberFormat allowClear decimalScale={0} />
                </Form.Item>
              </Col>
            </Row>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Номер СФ",
        fieldElement: (
          <Form.Item label="Номер СФ" name="invoiceExtNum">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Период счета",
        fieldElement: (
          <Form.Item label="Период счета" name="billPeriod">
            <DatePicker.RangePicker style={{ width: "100%" }} format={displayFormat} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID начислений",
        fieldElement: (
          <Form.Item
            label="ID начислений"
            name="chargeIds"
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: "В качестве разделителя может использоваться только запятая",
              },
            ]}
          >
            <Input allowClear placeholder={`Введите ID начислений через "," `} />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: tenant?.id === 1 ? "БИН" : "ИНН",
        fieldElement: (
          <ActiveSearchFormItem
            name="organization.property.stringValue"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "subscriber.externalId": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: false,
      },
      {
        label: "Тех. ID",
        labelTitle: "Технический ID",
        fieldElement: (
          <ActiveSearchFormItem
            name="subscriber.externalId"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "organization.property.stringValue": undefined,
              });
            }}
            clientType={clientType}
          />
        ),
        visible: true,
      },
    ],
    [
      rateModesEvents,
      rateModesEventsLoading,
      serviceTypes,
      serviceTypesFetchingState,
      rateTypeLoading,
      rateType,
      isAdjustmentList,
      tenant,
      clientType,
    ]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <div className="charges-search-filters">
      <div className="inner-row">
        <Collapse
          defaultActiveKey="1"
          onChange={toggleFiltersCollapseState}
          expandIconPosition="right"
          className="filter-collapse"
        >
          <Collapse.Panel header={getPanelHeader()} key="1">
            <Row justify="end">
              <BulkFiltersToggler
                allFields={allFields}
                setFields={setFields}
                filterName="bulk-charges"
              />
            </Row>
            <Form
              form={form}
              labelAlign="left"
              initialValues={defaultSearchParams}
              {...layout}
              onFinish={onSearch}
            >
              <Row gutter={50}>
                {fields
                  .filter(({ visible }) => visible)
                  .map(({ fieldElement }, idx) => (
                    <Col span={8} key={idx}>
                      {fieldElement}
                    </Col>
                  ))}
              </Row>
              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={resetSearchPaymentsAndResetSetSelectedRowKeys}>
                  Сброс параметров
                </Button>
                <Button
                  loading={checkIsLoading(chargesMassLoading)}
                  htmlType="submit"
                  type="primary"
                >
                  Применить
                </Button>
              </Form.Item>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
};
